import { useEffect, useState, type FocusEvent } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Flag as GoalIcon,
  Percent as PercentIcon,
  Savings as BudgetIcon,
  Timeline as TimelineIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCircle,
  deleteCircle,
  exportCircles,
  getCircles,
  getCircleStats,
  updateBudget,
  updateProfitGoal,
} from '@/services/api';
import { MetricCard, PageShell, SectionHeader } from '@/components/common/Page';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import type { Circle, CreateCircleRequest } from '@/types';
import { formatUsd } from '@/utils/format';

interface PrefillCircleState {
  pair_id?: number;
  coin_from?: string;
  coin_to?: string;
  exchange_buy?: string;
  exchange_sell?: string;
  pair_type?: string;
}

const selectNumberInput = (event: FocusEvent<HTMLInputElement>) => {
  event.target.select();
};

const normalizeDecimalInput = (value: string) => {
  if (!value) return '';
  return value.replace(/^(-?)0+(?=\d)/, '$1');
};

const numberInputProps = {
  inputMode: 'decimal' as const,
};

export default function CalculatorPage() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, refetch: refetchAuth } = useAuth();
  const { formatDate, plural, t } = useI18n();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [budgetValue, setBudgetValue] = useState(user?.budget ? String(user.budget) : '');
  const [goalValue, setGoalValue] = useState(user?.monthly_profit_goal ? String(user.monthly_profit_goal) : '');

  const [newCircle, setNewCircle] = useState<CreateCircleRequest>({
    amount_invested: 0,
    amount_received: 0,
    coin_from: '',
    exchange_buy: '',
    exchange_sell: '',
    notes: '',
  });

  useEffect(() => {
    setBudgetValue(user?.budget ? String(user.budget) : '');
  }, [user?.budget]);

  useEffect(() => {
    setGoalValue(user?.monthly_profit_goal ? String(user.monthly_profit_goal) : '');
  }, [user?.monthly_profit_goal]);

  useEffect(() => {
    const state = location.state as { prefillCircle?: PrefillCircleState } | null;
    if (state?.prefillCircle) {
      const prefill = state.prefillCircle;
      setNewCircle({
        amount_invested: 0,
        amount_received: 0,
        pair_id: prefill.pair_id,
        coin_from: prefill.coin_from || '',
        coin_to: prefill.coin_to,
        exchange_buy: prefill.exchange_buy || '',
        exchange_sell: prefill.exchange_sell || '',
        pair_type: prefill.pair_type,
        notes: '',
      });
      setOpenAddDialog(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const { data: circlesData, isLoading: circlesLoading } = useQuery({
    queryKey: ['circles'],
    queryFn: () => getCircles(50, 0),
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['circle-stats'],
    queryFn: getCircleStats,
  });

  const createMutation = useMutation({
    mutationFn: createCircle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circles'] });
      queryClient.invalidateQueries({ queryKey: ['circle-stats'] });
      setOpenAddDialog(false);
      setNewCircle({
        amount_invested: 0,
        amount_received: 0,
        coin_from: '',
        exchange_buy: '',
        exchange_sell: '',
        notes: '',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCircle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circles'] });
      queryClient.invalidateQueries({ queryKey: ['circle-stats'] });
    },
  });

  const budgetMutation = useMutation({
    mutationFn: updateBudget,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circle-stats'] });
      refetchAuth();
      setEditingBudget(false);
    },
  });

  const goalMutation = useMutation({
    mutationFn: updateProfitGoal,
    onSuccess: () => {
      refetchAuth();
      setEditingGoal(false);
    },
  });

  const handleExport = async () => {
    try {
      const blob = await exportCircles();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${t('calculator.exportFilePrefix')}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const stats = statsData?.stats;
  const budget = statsData?.budget || 0;
  const monthlyGoal = user?.monthly_profit_goal || 0;
  const last30Profit = stats?.last_30_days.profit || 0;
  const goalProgress = monthlyGoal > 0 ? (last30Profit / monthlyGoal) * 100 : 0;
  const projectedPercent =
    stats?.projected_monthly_profit && budget > 0
      ? (stats.projected_monthly_profit / budget) * 100
      : 0;
  const formatCurrency = formatUsd;
  const roundWord = (count: number) =>
    plural(count, {
      one: 'common.roundOne',
      few: 'common.roundFew',
      other: 'common.roundOther',
    });

  return (
    <PageShell
      eyebrow={t('calculator.eyebrow')}
      title={t('calculator.title')}
      showAnnouncements
      action={
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('calculator.exportCsv')}>
            <IconButton onClick={handleExport} aria-label={t('calculator.exportCsv')}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}>
            {t('calculator.logRound')}
          </Button>
        </Stack>
      }
    >
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                {t('calculator.budget')}
              </Typography>
              {editingBudget ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={budgetValue}
                    onFocus={selectNumberInput}
                    onChange={(event) => setBudgetValue(normalizeDecimalInput(event.target.value))}
                    inputProps={numberInputProps}
                    placeholder="0"
                  />
                  <Button
                    variant="contained"
                    onClick={() => budgetMutation.mutate(parseFloat(budgetValue) || 0)}
                    disabled={budgetMutation.isPending}
                  >
                    {t('common.save')}
                  </Button>
                  <Button variant="outlined" onClick={() => setEditingBudget(false)}>
                    {t('common.cancel')}
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.5 }}>
                  <Typography variant="h4">{formatCurrency(budget)}</Typography>
                  <Button size="small" variant="outlined" onClick={() => setEditingBudget(true)}>
                    {t('common.edit')}
                  </Button>
                </Stack>
              )}
            </Box>

            {stats && stats.projected_monthly_profit > 0 && (
              <Chip
                icon={<TrendingUpIcon />}
                label={t('calculator.projected', {
                  amount: formatCurrency(stats.projected_monthly_profit),
                  percent: projectedPercent.toFixed(1),
                })}
                color="success"
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <MetricCard
              label={t('calculator.totalRounds')}
              value={stats.total_circles}
              icon={<TimelineIcon fontSize="small" />}
              tone="info"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label={t('calculator.averageProfit')}
              value={`${stats.average_percent.toFixed(2)}%`}
              icon={<PercentIcon fontSize="small" />}
              tone={stats.average_percent >= 0 ? 'success' : 'error'}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label={t('calculator.totalProfit')}
              value={formatCurrency(stats.total_profit)}
              icon={<BudgetIcon fontSize="small" />}
              tone={stats.total_profit >= 0 ? 'success' : 'error'}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricCard
              label={t('calculator.last30Days')}
              value={formatCurrency(stats.last_30_days.profit)}
              helper={`${stats.last_30_days.count} ${roundWord(stats.last_30_days.count)}`}
              icon={<GoalIcon fontSize="small" />}
              tone={stats.last_30_days.profit >= 0 ? 'success' : 'warning'}
            />
          </Grid>
        </Grid>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1">{t('calculator.monthlyGoal')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(last30Profit)} / {formatCurrency(monthlyGoal)}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => setEditingGoal((value) => !value)}>
              {editingGoal ? t('common.close') : t('common.edit')}
            </Button>
          </Stack>

          {editingGoal ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                type="number"
                value={goalValue}
                onFocus={selectNumberInput}
                onChange={(event) => setGoalValue(normalizeDecimalInput(event.target.value))}
                inputProps={numberInputProps}
                placeholder={t('calculator.monthlyGoalPlaceholder')}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => goalMutation.mutate(parseFloat(goalValue) || 0)}
                disabled={goalMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </Stack>
          ) : (
            <>
              <LinearProgress
                variant="determinate"
                value={Math.min(Math.max(goalProgress, 0), 100)}
                color={goalProgress >= 100 ? 'success' : 'primary'}
                sx={{ height: 10 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {monthlyGoal > 0
                  ? t('calculator.progressComplete', { percent: goalProgress.toFixed(0) })
                  : t('calculator.noMonthlyGoal')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <SectionHeader
        title={t('calculator.recentRounds')}
        detail={t('calculator.recentLogged', { count: circlesData?.total ?? 0 })}
      />

      {circlesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : circlesData?.circles.length === 0 ? (
        <Alert severity="info">{t('calculator.noRounds')}</Alert>
      ) : (
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {circlesData?.circles.map((circle: Circle) => {
            const isProfit = circle.profit_percent >= 0;

            return (
              <Card key={circle.id}>
                <ListItem
                  sx={{ py: 1.25, pr: 7 }}
                  secondaryAction={
                    <Tooltip title={t('calculator.deleteRound')}>
                      <IconButton
                        edge="end"
                        onClick={() => deleteMutation.mutate(circle.id)}
                        disabled={deleteMutation.isPending}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                        {isProfit ? (
                          <TrendingUpIcon color="success" fontSize="small" />
                        ) : (
                          <TrendingDownIcon color="error" fontSize="small" />
                        )}
                        <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {circle.coin_from || t('common.unknown')}
                          {circle.exchange_buy && circle.exchange_sell
                            ? ` (${circle.exchange_buy} -> ${circle.exchange_sell})`
                            : ''}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${isProfit ? '+' : ''}${circle.profit_percent.toFixed(2)}%`}
                          color={isProfit ? 'success' : 'error'}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(circle.amount_invested)} {'->'} {formatCurrency(circle.amount_received)} |{' '}
                        {formatDate(circle.created_at)}
                        {circle.notes ? ` | ${circle.notes}` : ''}
                      </Typography>
                    }
                  />
                </ListItem>
              </Card>
            );
          })}
        </List>
      )}

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('calculator.logRound')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('calculator.coin')}
              placeholder={t('calculator.coinPlaceholder')}
              value={newCircle.coin_from}
              onChange={(event) => setNewCircle({ ...newCircle, coin_from: event.target.value.toUpperCase() })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('calculator.exchangeBuy')}
                placeholder={t('calculator.exchangeBuyPlaceholder')}
                value={newCircle.exchange_buy}
                onChange={(event) => setNewCircle({ ...newCircle, exchange_buy: event.target.value })}
                fullWidth
              />
              <TextField
                label={t('calculator.exchangeSell')}
                placeholder={t('calculator.exchangeSellPlaceholder')}
                value={newCircle.exchange_sell}
                onChange={(event) => setNewCircle({ ...newCircle, exchange_sell: event.target.value })}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('calculator.amountInvested')}
                type="number"
                value={newCircle.amount_invested || ''}
                onFocus={selectNumberInput}
                onChange={(event) =>
                  setNewCircle({ ...newCircle, amount_invested: parseFloat(event.target.value) || 0 })
                }
                inputProps={numberInputProps}
                fullWidth
              />
              <TextField
                label={t('calculator.amountReceived')}
                type="number"
                value={newCircle.amount_received || ''}
                onFocus={selectNumberInput}
                onChange={(event) =>
                  setNewCircle({ ...newCircle, amount_received: parseFloat(event.target.value) || 0 })
                }
                inputProps={numberInputProps}
                fullWidth
              />
            </Stack>
            {newCircle.amount_invested > 0 && newCircle.amount_received > 0 && (
              <Alert severity={newCircle.amount_received >= newCircle.amount_invested ? 'success' : 'warning'}>
                {t('calculator.profit')}:{' '}
                {(
                  ((newCircle.amount_received - newCircle.amount_invested) / newCircle.amount_invested) *
                  100
                ).toFixed(2)}
                % ({formatCurrency(newCircle.amount_received - newCircle.amount_invested)})
              </Alert>
            )}
            <TextField
              label={t('calculator.notes')}
              multiline
              rows={2}
              value={newCircle.notes}
              onChange={(event) => setNewCircle({ ...newCircle, notes: event.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate(newCircle)}
            disabled={createMutation.isPending || !newCircle.amount_invested || !newCircle.amount_received}
          >
            {t('calculator.logRound')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
